import React, { useState } from 'react'
import { Loader2, StopCircle, CheckCircle2, AlertTriangle, XCircle, Activity, ChevronDown, Clock, Repeat } from 'lucide-react'
import { useActivitySagas, useCancelSaga, usePollers } from '../hooks/useActivity'

// All durable background work is a control-plane saga. This is the one place to
// watch it — progress survives switching tabs / closing the panel that started
// it, which the per-feature progress bars can't.
const SAGA_LABEL = {
  'ask': 'Question (async)',
  'transcribe-batch': 'Bulk transcription',
  'index-transcripts': 'Indexing transcripts',
  'import-index': 'Import & index',
  'index-codebase': 'Indexing codebase',
  'sync-codebase': 'Syncing codebase',
  'code-docs': 'Generating docs',
  'delete-project': 'Deleting project',
}
const ACTIVE = new Set(['running', 'compensating'])

// Best-effort {done,total,phase} across the different saga shapes — each type
// names its progress fields differently (cursor / transcribed / indexed / …).
function sagaProgress(saga) {
  const steps = saga.steps ?? []
  const step = steps.find((s) => s.status === 'running') ?? steps[steps.length - 1]
  const d = step?.data ?? {}
  const total = d.total ?? null
  const failedN = Array.isArray(d.failed) ? d.failed.length : d.failed ?? 0
  let done = null
  if (d.cursor != null) done = d.cursor
  else if (d.transcribed != null) done = d.transcribed + failedN
  else if (d.indexed != null) done = d.indexed + (d.skipped ?? 0)
  const pct = total ? Math.round(((done ?? 0) / total) * 100) : null
  return { stepName: step?.name, phase: d.phase ?? null, done, total, pct, data: d }
}

function relTime(iso) {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const s = Math.max(0, Math.round((Date.now() - t) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

function inTime(ms) {
  if (!ms) return ''
  const s = Math.round((ms - Date.now()) / 1000)
  if (s <= 0) return 'due'
  if (s < 60) return `in ${s}s`
  if (s < 3600) return `in ${Math.round(s / 60)}m`
  if (s < 86400) return `in ${Math.round(s / 3600)}h`
  return `in ${Math.round(s / 86400)}d`
}
function humanInterval(ms) {
  if (!ms) return '—'
  const h = ms / 3600000
  if (h >= 1) return `${Number.isInteger(h) ? h : h.toFixed(1)}h`
  return `${Math.round(ms / 60000)}m`
}

function PollerRow({ p }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-parchment-border dark:border-midnight-600 bg-white/40 dark:bg-midnight-800/30 px-4 py-2 text-sm">
      <Repeat className={`w-4 h-4 ${p.enabled ? 'text-cyan-600 dark:text-cyan-400' : 'text-ink-soft/40'}`} />
      <span className="text-ink dark:text-mist font-medium">{p.label}</span>
      <span className="text-xs text-ink-soft dark:text-mist/50">every {humanInterval(p.interval_ms)}</span>
      {!p.enabled && <span className="text-xs px-1.5 py-0.5 rounded bg-ink-soft/10 text-ink-soft dark:text-mist/50">off</span>}
      <div className="ml-auto flex items-center gap-3 text-xs text-ink-soft dark:text-mist/50">
        {p.last_error && <span className="text-red-600" title={p.last_error}>last: error</span>}
        {!p.last_error && p.last_tick_at && <span title={p.last_result || ''}>ran {relTime(new Date(p.last_tick_at).toISOString())}</span>}
        {p.enabled && p.next_tick_at && (
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{inTime(p.next_tick_at)}</span>
        )}
      </div>
    </div>
  )
}

function StatusIcon({ status }) {
  if (ACTIVE.has(status)) return <Loader2 className="w-4 h-4 animate-spin text-cyan-600 dark:text-cyan-400" />
  if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
  if (status === 'failed') return <AlertTriangle className="w-4 h-4 text-red-600" />
  return <XCircle className="w-4 h-4 text-ink-soft dark:text-mist/50" /> // cancelled / compensated
}

function SagaCard({ saga }) {
  const [open, setOpen] = useState(false)
  const cancel = useCancelSaga()
  const active = ACTIVE.has(saga.status)
  const p = sagaProgress(saga)
  const label = SAGA_LABEL[saga.type] ?? saga.type

  return (
    <div className="rounded-xl border border-parchment-border dark:border-midnight-600 bg-white/60 dark:bg-midnight-800/40 px-4 py-3">
      <div className="flex items-center gap-3">
        <StatusIcon status={saga.status} />
        <div className="min-w-0">
          <div className="text-sm text-ink dark:text-mist">
            <span className="font-medium">{label}</span>
            {saga.project && <span className="text-ink-soft dark:text-mist/50"> · {saga.project}</span>}
            <span className="text-ink-soft dark:text-mist/40 text-xs"> · #{saga.id}</span>
          </div>
          <div className="text-xs text-ink-soft dark:text-mist/50">
            {saga.status}{p.phase ? ` · ${p.phase}` : ''} · updated {relTime(saga.updated_at)}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {active && (
            <button
              onClick={() => cancel.mutate(saga.id)}
              disabled={cancel.isPending}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 disabled:opacity-50"
            >
              <StopCircle className="w-3.5 h-3.5" /> Stop
            </button>
          )}
          <button onClick={() => setOpen((v) => !v)} className="text-ink-soft hover:text-ink dark:text-mist/50" aria-label="steps">
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {(active || p.total != null) && p.total != null && (
        <div className="mt-2">
          <div className="h-1.5 rounded-full bg-parchment-border dark:bg-midnight-600 overflow-hidden">
            <div className="h-full bg-cyan-500 transition-all" style={{ width: `${p.pct ?? (active ? 0 : 100)}%` }} />
          </div>
          <div className="mt-1 text-xs text-ink-soft dark:text-mist/50">
            {p.done ?? 0}/{p.total}
            {p.data.transcribed != null && <> · transcribed {p.data.transcribed}</>}
            {p.data.indexed != null && <> · indexed {p.data.indexed}</>}
            {(p.data.partial?.length ?? p.data.partial ?? 0) > 0 && <> · <span className="text-amber-600">partial {p.data.partial?.length ?? p.data.partial}</span></>}
            {(p.data.failed?.length ?? p.data.failed ?? 0) > 0 && <> · <span className="text-red-600">failed {p.data.failed?.length ?? p.data.failed}</span></>}
          </div>
        </div>
      )}

      {saga.status === 'failed' && saga.error && (
        <p className="mt-2 text-xs text-red-600">{saga.error}</p>
      )}

      {open && (
        <div className="mt-2 border-t border-parchment-border dark:border-midnight-600 pt-2 space-y-1">
          {(saga.steps ?? []).length === 0 && <p className="text-xs text-ink-soft dark:text-mist/40 italic">No step detail (terminal saga).</p>}
          {(saga.steps ?? []).map((s) => (
            <div key={s.seq} className="text-xs flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${s.status === 'done' ? 'bg-emerald-500' : s.status === 'running' ? 'bg-cyan-500' : s.status === 'failed' ? 'bg-red-500' : 'bg-ink-soft/40'}`} />
              <span className="font-mono text-ink dark:text-mist">{s.name}</span>
              <span className="text-ink-soft dark:text-mist/50">{s.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ActivityPage() {
  const q = useActivitySagas()
  const pollersQ = usePollers()
  const sagas = q.data ?? []
  const pollers = pollersQ.data ?? []
  const active = sagas.filter((s) => ACTIVE.has(s.status))
  const recent = sagas.filter((s) => !ACTIVE.has(s.status))

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Activity className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
        <h1 className="font-display text-2xl font-semibold text-ink dark:text-mist">Activity</h1>
        <span className="text-sm text-ink-soft dark:text-mist/50">durable background work — imports, transcription, indexing, docs</span>
      </div>

      {q.isLoading && <div className="flex items-center gap-2 text-sm text-ink-soft dark:text-mist/60"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
      {q.isError && <p className="text-sm text-red-600">{q.error?.message}</p>}

      <section className="space-y-2">
        <h2 className="text-xs uppercase tracking-wide text-ink-soft dark:text-mist/50">Running ({active.length})</h2>
        {active.length === 0 && <p className="text-sm text-ink-soft dark:text-mist/50 italic">Nothing running right now.</p>}
        {active.map((s) => <SagaCard key={s.id} saga={s} />)}
      </section>

      {pollers.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs uppercase tracking-wide text-ink-soft dark:text-mist/50">Scheduled (recurring)</h2>
          {pollers.map((p) => <PollerRow key={p.name} p={p} />)}
        </section>
      )}

      {recent.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs uppercase tracking-wide text-ink-soft dark:text-mist/50">Recent</h2>
          {recent.map((s) => <SagaCard key={s.id} saga={s} />)}
        </section>
      )}
    </div>
  )
}
